"use client"

import { useState } from "react"
import { Input } from "../../../Catalyst/input"
import SendInviteIcon from "../../../../assets/SendInviteIcon"
import { Switch } from "../../../Catalyst/switch"
import { createUser } from "../../../../services/UsersService"
import { useToast } from "../../../../contexts/ToastContext"
import { AccessibleOrganization } from "../../../../store/slices/OrganizationsV3Slice"
import OrgSelectPopover from "../../../common/OrgSelectPopover"
import SingleSelectDropdown from "../../../common/SingleSelectDropdown"

interface InviteManualSectionProps {
  // Preselected when the modal is opened from an organization's page.
  defaultOrganizationId?: number | null
  organizationOptions: AccessibleOrganization[]
  isOrganizationOptionsLoading: boolean
  isOrganizationOptionsError: boolean
  roleOptions: { value: string; label: string }[]
  // Called after a successful invite so the user list can refresh.
  onInvited?: () => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const labelClass = "block font-['Inter'] font-medium text-[14px] leading-[20px] text-slate-900 mb-1.5"

export function InviteManualSection({
  defaultOrganizationId = null,
  organizationOptions,
  isOrganizationOptionsLoading,
  isOrganizationOptionsError,
  roleOptions,
  onInvited,
}: InviteManualSectionProps) {
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [organizationId, setOrganizationId] = useState<number | null>(defaultOrganizationId)
  const [role, setRole] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccessToast, showErrorToast } = useToast()

  const trimmedEmail = email.trim()
  const showEmailError = trimmedEmail.length > 0 && !EMAIL_PATTERN.test(trimmedEmail)
  // AC: "Invite User" stays disabled until every field is filled.
  const isFormValid =
    name.trim().length > 0 && EMAIL_PATTERN.test(trimmedEmail) && organizationId != null && role != null

  const handleInvite = async () => {
    if (!isFormValid || isLoading || organizationId == null || !role) return

    setIsLoading(true)
    try {
      // The single "Full Name" field is split into first / last on the first space.
      const [first, ...rest] = name.trim().split(/\s+/)
      await createUser({
        organization_id: organizationId,
        email: trimmedEmail,
        first,
        last: rest.join(" "),
        user_role: role,
        send_invite: sendWelcomeEmail,
      })

      // Copy per the ADM-92 Figma.
      showSuccessToast(
        "You've added (1) Employee",
        sendWelcomeEmail
          ? "You can view the status of this new invite in your dashboard."
          : "No welcome email was sent. You can view the new user in your dashboard and invite them anytime."
      )
      onInvited?.()

      // Ready for the next invite: keep the org and role, clear the person.
      setName("")
      setEmail("")
      setSendWelcomeEmail(true)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.msg || err?.response?.data?.message || "Failed to create user. Please try again."
      showErrorToast('Failed to create user', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h3 className="font-['Inter'] text-[14px] font-medium leading-[20px] text-[#525252] mb-1">Send Invites Manually</h3>
      <p className="font-['Inter'] text-[12px] font-medium leading-[20px] text-[#525252] mb-3">
        Sending an invite manually is a great way to add one more users. They will receive an email to access their
        Finley account.
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[160px] flex-1">
          <label htmlFor="invite-name" className={labelClass}>
            User's Full Name
          </label>
          <Input
            id="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full Name"
            className="border-gray-300 text-gray-900 placeholder:text-gray-400"
            disabled={isLoading}
          />
        </div>
        <div className="min-w-[160px] flex-1">
          <label htmlFor="invite-email" className={labelClass}>
            User's Email
          </label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            invalid={showEmailError}
            className="border-gray-300 text-gray-900 placeholder:text-gray-400"
            disabled={isLoading}
          />
        </div>
        <div>
          <span className={labelClass}>Organization</span>
          <OrgSelectPopover
            options={organizationOptions}
            value={organizationId}
            onChange={setOrganizationId}
            isLoading={isOrganizationOptionsLoading}
            isError={isOrganizationOptionsError}
            disabled={isLoading}
          />
        </div>
        <div>
          <span className={labelClass}>Role</span>
          <SingleSelectDropdown label="Role" options={roleOptions} value={role} onChange={setRole} />
        </div>
      </div>
      {showEmailError && <p className="-mt-1 mb-3 text-sm text-red-600">Enter a valid email address.</p>}

      <div className="flex items-center justify-end gap-3">
        <div className={`flex items-start gap-2 ${isLoading ? 'opacity-50' : ''}`}>
          <Switch
            color="green"
            checked={sendWelcomeEmail}
            onChange={setSendWelcomeEmail}
            disabled={isLoading}
            aria-label="Send welcome email for the manual invite"
          />
          <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#344054]">Send welcome email</span>
        </div>
        <div className={`${!isFormValid || isLoading ? 'opacity-50' : ''}`}>
          <button
            onClick={handleInvite}
            disabled={!isFormValid || isLoading}
            title={!isFormValid ? "Fill in the name, email, organization and role" : undefined}
            className="bg-white border border-neutral-200 rounded-lg shadow-sm px-3.5 py-2.5 flex items-center gap-1 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
          >
            <SendInviteIcon />
            <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-neutral-900">
              {isLoading ? 'Sending...' : 'Invite User'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
